// client/src/components/PrintQueue.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Form, Modal, Badge } from 'react-bootstrap';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function PrintQueue({ addAlert }) {
  const [printJobs, setPrintJobs] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [newPrintJob, setNewPrintJob] = useState({
    design_id: '',
    labels_to_print: 100
  });

  useEffect(() => {
    fetchPrintJobs();
    fetchDesigns();
  }, []);

  const fetchPrintJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/print/queue.php`);
      setPrintJobs(response.data.records || []);
    } catch (error) {
      console.error('Error fetching print jobs:', error);
      addAlert('Failed to load print queue', 'danger');
    }
  };

  const fetchDesigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/designs/read.php`);
      setDesigns(response.data.records || []);
    } catch (error) {
      console.error('Error fetching designs:', error);
    }
  };

  const handlePrintSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API_URL}/print/create.php`, newPrintJob);
      addAlert('Print job queued successfully!', 'success');
      setShowPrintModal(false);
      setNewPrintJob({ design_id: '', labels_to_print: 100 });
      fetchPrintJobs();
    } catch (error) {
      console.error('Error creating print job:', error);
      addAlert('Failed to create print job', 'danger');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Pending': return <Badge bg="warning">Pending</Badge>;
      case 'Processing': return <Badge bg="info">Processing</Badge>;
      case 'Completed': return <Badge bg="success">Completed</Badge>;
      case 'Failed': return <Badge bg="danger">Failed</Badge>;
      default: return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not printed';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Print Queue</h2>
        <Button variant="purple" onClick={() => setShowPrintModal(true)}>
          <i className="fas fa-print me-2"></i>
          New Print Job
        </Button>
      </div>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Current Print Jobs</Card.Title>
              {printJobs.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-print fa-3x mb-3"></i>
                  <p>No print jobs in queue</p>
                </div>
              ) : (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Design</th>
                      <th>SKU</th>
                      <th>Labels</th>
                      <th>Status</th>
                      <th>Printed At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printJobs.map((job) => (
                      <tr key={job.id}>
                        <td>#{job.id}</td>
                        <td>
                          <div>{job.design_name}</div>
                          <div className="small text-muted">{job.brand_name}</div>
                        </td>
                        <td>
                          <code>{job.sku_code}</code>
                        </td>
                        <td>
                          <strong>{job.labels_to_print}</strong>
                        </td>
                        <td>{getStatusBadge(job.print_status)}</td>
                        <td>{formatDate(job.printed_at)}</td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-1">
                            <i className="fas fa-play"></i>
                          </Button>
                          <Button size="sm" variant="outline-danger">
                            <i className="fas fa-times"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Print Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>New Print Job</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePrintSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Select Design *</Form.Label>
              <Form.Select
                value={newPrintJob.design_id}
                onChange={(e) => setNewPrintJob({...newPrintJob, design_id: e.target.value})}
                required
              >
                <option value="">Choose a design...</option>
                {designs.map((design) => (
                  <option key={design.id} value={design.id}>
                    {design.design_name} ({design.sku_code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Number of Labels to Print *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="5000"
                value={newPrintJob.labels_to_print}
                onChange={(e) => setNewPrintJob({...newPrintJob, labels_to_print: e.target.value})}
                required
              />
              <Form.Text className="text-muted">
                TVS LP46 printer supports 50mm × 80mm dress labels
              </Form.Text>
            </Form.Group>

            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              This will send the print job to the TVS LP46 printer queue
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>
              Cancel
            </Button>
            <Button variant="purple" type="submit">
              <i className="fas fa-paper-plane me-2"></i>
              Queue Print Job
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default PrintQueue;